import { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { useEventCoverUpload } from "@/lib/api/useEventCoverUpload";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif";
const ACCEPTED_MIMES = ACCEPT.split(",");
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

interface Props {
  value: string;
  onChange: (url: string) => void;
  onError?: (message: string) => void;
  // "square" shows a 1:1 preview with the whole image fitted (club covers);
  // the default is a cropped 16:9 event cover.
  shape?: "wide" | "square";
}

export function CoverImageUpload({ value, onChange, onError, shape = "wide" }: Props) {
  const aspect = shape === "square" ? "aspect-square" : "aspect-[16/9]";
  const upload = useEventCoverUpload();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [fetching, setFetching] = useState(false);

  const handleFile = async (file: File) => {
    if (!ACCEPTED_MIMES.includes(file.type)) {
      onError?.("That image format isn't supported. Try JPEG, PNG, WebP, or GIF.");
      return;
    }
    if (file.size > MAX_BYTES) {
      onError?.("That image is too large. Please pick one under 10MB.");
      return;
    }
    try {
      const url = await upload.mutateAsync(file);
      onChange(url);
    } catch (err) {
      onError?.((err as Error).message || "Upload failed");
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    try {
      setFetching(true);
      const file = await extractFileFromDataTransfer(e.dataTransfer);
      setFetching(false);
      if (!file) {
        onError?.("That doesn't look like an image we can use.");
        return;
      }
      await handleFile(file);
    } catch (err) {
      setFetching(false);
      onError?.((err as Error).message || "Couldn't read that image");
    }
  };

  // Shared drag-drop handlers — applied to both empty and filled states so
  // dragging onto an existing cover replaces it.
  const dragProps = {
    onDragEnter: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(true);
    },
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // dataTransfer.dropEffect must be set during dragover for some browsers
      // (notably Safari) to fire the drop event reliably.
      e.dataTransfer.dropEffect = "copy";
      setDragOver(true);
    },
    onDragLeave: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
    },
    onDrop: handleDrop,
  };

  const busy = upload.isPending || fetching;

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept={ACCEPT}
      className="hidden"
      onChange={(e) => {
        const f = e.target.files?.[0];
        if (f) handleFile(f);
      }}
    />
  );

  // Filled state ----------------------------------------------------------
  if (value) {
    return (
      <div
        {...dragProps}
        className={`relative group rounded-xl overflow-hidden ${aspect} bg-base-300 ${
          dragOver ? "ring-4 ring-primary-500/60" : ""
        }`}
      >
        <img src={value} alt="Cover" className={`w-full h-full ${shape === "square" ? "object-contain" : "object-cover"}`} />

        {dragOver && (
          <div className="absolute inset-0 bg-primary-500/30 backdrop-blur-sm flex items-center justify-center pointer-events-none">
            <div className="px-4 py-2 rounded-full bg-black/60 text-white text-sm font-medium">
              Drop to replace
            </div>
          </div>
        )}

        {busy && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-white" />
          </div>
        )}

        <button
          type="button"
          onClick={() => onChange("")}
          className="absolute top-2 right-2 p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
          aria-label="Remove image"
        >
          <X className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="absolute bottom-2 right-2 px-3 py-1.5 rounded-full bg-black/60 text-white text-xs hover:bg-black/80 transition-colors"
        >
          Replace
        </button>
        {fileInput}
      </div>
    );
  }

  // Empty state -----------------------------------------------------------
  return (
    <div
      {...dragProps}
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      className={`
        w-full ${aspect} rounded-xl
        flex flex-col items-center justify-center gap-2 cursor-pointer
        transition-all select-none
        ${
          dragOver
            ? "bg-gradient-to-br from-primary-500/25 via-primary-500/15 to-secondary-500/20 ring-2 ring-primary-500/40"
            : "bg-gradient-to-br from-primary-500/10 via-secondary-500/5 to-accent-500/10 hover:from-primary-500/15 hover:to-accent-500/15"
        }
      `}
    >
      {busy ? (
        <>
          <Loader2 className="w-7 h-7 animate-spin text-primary-500" />
          <div className="text-sm text-neutral-500 dark:text-neutral-400">
            {fetching ? "Fetching image…" : "Uploading…"}
          </div>
        </>
      ) : (
        <>
          <ImagePlus className="w-8 h-8 text-neutral-400" />
          <div className="text-sm font-medium">
            {dragOver ? "Drop to upload" : "Add a cover image"}
          </div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400 px-4 text-center">
            Click, drop a file, or drag an image from another tab · JPG, PNG, WebP, GIF · up to 10MB
          </div>
        </>
      )}
      {fileInput}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DataTransfer extraction — handles three drop scenarios:
//   1. Local file from OS → dataTransfer.files
//   2. Image dragged from another browser tab → typically only URLs in
//      text/uri-list, text/plain, or an <img> inside text/html
//   3. Some browsers expose images dragged from tabs as a kind:'file' item
//
// URL-based drops require fetching the image bytes ourselves, which is
// subject to CORS. Many remote hosts will block this; we surface a friendly
// error and the user falls back to saving to disk + drag-from-OS.
// ---------------------------------------------------------------------------

async function extractFileFromDataTransfer(dt: DataTransfer): Promise<File | null> {
  // 1. Local-file drop
  if (dt.files && dt.files.length > 0) {
    return dt.files[0];
  }

  // 2. Item-list with a file (some cross-tab drags)
  for (let i = 0; i < dt.items.length; i++) {
    const item = dt.items[i];
    if (item.kind === "file") {
      const f = item.getAsFile();
      if (f) return f;
    }
  }

  // 3. URL-based drop
  const url = extractUrlFromDataTransfer(dt);
  if (!url) return null;
  return fetchUrlAsFile(url);
}

function extractUrlFromDataTransfer(dt: DataTransfer): string | null {
  // text/uri-list is the standard format for URL drags. It may contain
  // multiple lines and comments (lines starting with #).
  const uriList = dt.getData("text/uri-list");
  if (uriList) {
    const firstUrl = uriList
      .split(/\r?\n/)
      .map((line) => line.trim())
      .find((line) => line && !line.startsWith("#"));
    if (firstUrl) return firstUrl;
  }

  // text/html is what's set when you drag an <img> element. Pull the first src.
  const html = dt.getData("text/html");
  if (html) {
    const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
    if (match) return match[1];
  }

  // text/plain may be a raw URL.
  const plain = dt.getData("text/plain");
  if (plain && /^https?:\/\//i.test(plain.trim())) {
    return plain.trim();
  }

  return null;
}

async function fetchUrlAsFile(url: string): Promise<File> {
  let res: Response;
  try {
    res = await fetch(url, { mode: "cors" });
  } catch {
    throw new Error(
      "That image's host doesn't allow direct copying. Save it to your device, then drop the file here.",
    );
  }
  if (!res.ok) {
    throw new Error(`Couldn't fetch that image (${res.status})`);
  }
  const blob = await res.blob();
  if (!blob.type.startsWith("image/")) {
    throw new Error("That URL doesn't point to an image");
  }
  const ext = blob.type.split("/")[1]?.split("+")[0] || "jpg";
  const namePart = url.split("/").pop()?.split("?")[0] || `image.${ext}`;
  const finalName = namePart.includes(".") ? namePart : `${namePart}.${ext}`;
  return new File([blob], finalName, { type: blob.type });
}
